import React, { useCallback } from 'react';
import CircleIcon from '@mui/icons-material/Circle';

import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useScrollbarStyles } from 'modules/common/styles/useScrollbarStyles';
import { InsightsTrends } from '../InsightsTrends';
import TimeFormatComponent from './TimeFormatComponent';
import SourceAvatar from './SourceAvatar';
import { DatasetProp } from './UserSourceByTimeChart';

const useStyles = makeStyles((theme) => ({
  legendText: {
    paddingLeft: ({
      isMetricsByCollectors
    }: {
      isMetricsByCollectors: boolean;
    }) => (!isMetricsByCollectors ? theme.spacing(2) : undefined),
    color: theme.palette.text.primary
  },
  container: {
    width: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`
  },
  chartContainer: {
    paddingTop: theme.spacing(1)
  },
  legendContainer: {
    marginTop: theme.spacing(2),
    height: theme.spacing(56),
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    width: '100%'
  },
  legendRows: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5)
  },
  tooltip: {
    marginTop: `${theme.spacing(-2)} !important`
  },
  bulletIcon: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5)
  },
  columnHeader: {
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'flex-end'
  }
}));

type TrendsData = {
  currentTrendsData: Record<string, number>;
  prevTrendsData: Record<string, number>;
};

type LegendProps = {
  dataset: DatasetProp[][];
  isMetricsByCollectors: boolean;
  trendData?: TrendsData;
  billableTrendsData?: TrendsData;
  columnHeader?: string;
};
const Legend: React.FC<LegendProps> = ({
  dataset,
  isMetricsByCollectors,
  trendData,
  billableTrendsData,
  columnHeader
}: LegendProps) => {
  const classes = useStyles({ isMetricsByCollectors });
  const classesScrollbar = useScrollbarStyles();
  const displayTrends = useCallback(
    (id, name) => {
      return (
        <InsightsTrends
          prevData={trendData?.prevTrendsData[id] || 0}
          currentData={trendData?.currentTrendsData[id] || 0}
          label={name}
        />
      );
    },
    [trendData?.currentTrendsData, trendData?.prevTrendsData]
  );

  const displayBillingTrends = useCallback(
    (id, name) => {
      if (!billableTrendsData) {
        return undefined;
      }
      return (
        <>
          <span
            className={classes.columnHeader}
            data-testid="billable-utilization-by-source"
          >
            {`${billableTrendsData?.currentTrendsData[id]}%`}
          </span>
          <InsightsTrends
            prevData={billableTrendsData?.prevTrendsData[id] || 0}
            currentData={billableTrendsData?.currentTrendsData[id] || 0}
            label={name}
          />
        </>
      );
    },
    [billableTrendsData, classes.columnHeader]
  );

  return (
    <Grid container>
      <div
        className={classnames(
          classes.legendContainer,
          classesScrollbar.scrollbar
        )}
      >
        {columnHeader && (
          <Grid container paddingRight={2}>
            <Grid container item xs={billableTrendsData ? 6 : 8}>
              <div></div>
            </Grid>
            <Grid
              container
              item
              xs={billableTrendsData ? 3 : 4}
              className={classes.columnHeader}
            >
              <Typography variant="caption">{columnHeader}</Typography>
            </Grid>
            {billableTrendsData && (
              <Grid container item xs={3} className={classes.columnHeader}>
                <Typography variant="caption">
                  <FormattedMessage id="insights.timeBreakdownChart.billableUtilization" />
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
        {dataset.map((data) => (
          <Grid
            container
            key={JSON.stringify(data)}
            className={classes.legendRows}
          >
            <Grid
              item
              xs={billableTrendsData ? 6 : 8}
              container
              alignItems={'center'}
              data-testid={`legend-text-${data[0].name}`}
            >
              <CircleIcon
                fontSize="small"
                sx={{ color: data[0].color }}
                className={classes.bulletIcon}
              />
              {isMetricsByCollectors && (
                <SourceAvatar
                  name={data[0].name}
                  src={data[0].imgSrc}
                  data-testid={'source-avatar'}
                />
              )}
              <Grid item xs={8}>
                <Tooltip
                  title={data[0].name}
                  classes={{ popper: classes.tooltip }}
                >
                  <Typography
                    variant="body1"
                    className={classes.legendText}
                    noWrap={true}
                  >
                    {data[0].name}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={billableTrendsData ? 3 : 4}
              justifyContent={'flex-end'}
              alignItems={'center'}
              wrap="nowrap"
            >
              <span className={classes.columnHeader}>
                <TimeFormatComponent duration={data[0].value} />
              </span>
              {data[0]?.id &&
                trendData &&
                displayTrends(data[0]?.id, data[0].name)}
            </Grid>
            {billableTrendsData && data[0]?.id && (
              <Grid
                container
                item
                xs={3}
                justifyContent={'flex-end'}
                alignItems={'center'}
                wrap="nowrap"
              >
                {displayBillingTrends(data[0]?.id, data[0].name)}
              </Grid>
            )}
          </Grid>
        ))}
      </div>
    </Grid>
  );
};

export default React.memo(Legend);
